import { forwardRef, PropsWithoutRef, useImperativeHandle } from 'react';
import { createZodFormResolver, lt, showSuccessAlert, useFormControls } from '@mews/framework';
import { Stack } from '@optimus-web/core';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { PaymentFormProps } from '../types';
import { useDefaultFormContext } from '../useDefaultFormValues';
import preferences from '../../../../../preferences';
import { useAddMewsTerminalPayment } from '../queries';
import { useInvalidatePaymentsTableRecordsQueries } from '../../../../queries';
import { useInvalidateGetPaymentPlansTableRecords } from '../../../tables/PaymentPlans/queries';
import { MewsTerminalFormFields } from './MewsTerminalFormFields';
import { MewsTerminalFormField, MewsTerminalPaymentFormValues, createMewsTerminalPaymentFormSchema } from './schema';

export const MewsTerminalPaymentForm = forwardRef(
    ({ accountId, onSuccess, onSubmitting, onSubmitted }: PropsWithoutRef<PaymentFormProps>, ref) => {
        const dispatch = useCommanderThunkDispatch();
        const { getPreferredTerminalId } = preferences.selectors;
        const { setPreferredTerminalId } = preferences.actions;

        const preferedTerminalId = useCommanderSelector(getPreferredTerminalId);
        const invalidatePaymentsTableRecordsQueries = useInvalidatePaymentsTableRecordsQueries();
        const invalidateGetPaymentPlansTableRecords = useInvalidateGetPaymentPlansTableRecords();

        const { defaultFormValues } = useDefaultFormContext();
        const resolver = createZodFormResolver<MewsTerminalPaymentFormValues>(createMewsTerminalPaymentFormSchema);

        const addMewsTerminalPayment = useAddMewsTerminalPayment({ accountId });

        const { renderFormContent, hasDirtyFields, submit } = useFormControls<MewsTerminalPaymentFormValues>({
            onSubmit: async (formValues) => {
                try {
                    onSubmitting?.();

                    await addMewsTerminalPayment(formValues);

                    showSuccessAlert({
                        description: lt('PaymentTerminalCommandCreatedSuccessMessage'),
                    });

                    if (formValues.paymentTerminalId) {
                        dispatch(setPreferredTerminalId(formValues.paymentTerminalId));
                    }
                    invalidatePaymentsTableRecordsQueries({ accountId });
                    invalidateGetPaymentPlansTableRecords(formValues.paymentPlanId);

                    onSuccess?.();
                } finally {
                    onSubmitted?.();
                }
            },
            defaultValues: {
                ...defaultFormValues,
                [MewsTerminalFormField.PaymentTerminalId]: preferedTerminalId ?? null,
            },
            resolver,
        });

        useImperativeHandle(ref, () => ({
            submit,
            hasDirtyFields,
        }));
        return renderFormContent(
            <Stack spacing="300" vertical>
                <MewsTerminalFormFields />
            </Stack>
        );
    }
);
